import {useMutation, useQuery, useQueryClient, type UseQueryOptions} from '@tanstack/react-query';

import { ApiClient } from './api.client.js';
import { PatientsApiKeys } from './patients.api.js';

/** Streaming backend for a patient. `null` follows the environment default. */
export type MediaProviderName = 'antmedia' | 'mediamtx' | null;

export interface UpdateStreamPreferences {
  patientIds: string[];
}

export interface StreamPreferences {
  patients: MatchingPatients[];
}

export interface MatchingPatients {
  id: string;
  name: string;
  inMonitoringHours: boolean;
  selected: boolean;
}
export interface StreamData {
  streamId: string;
  token: string;
  hlsUrl?: string;
  vodUrl?: string;
}

export enum StreamQuality {
  HIGH = 'HIGH',
  LOW = 'LOW',
}

export const StreamsApiKeys = {
  GET_PREFERENCES: 'GET_PREFERENCES',
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',
  GET_STREAM_DATA: 'GET_STREAM_DATA',
  GET_STREAM_STATUS: 'GET_STREAM_STATUS',
  START_STREAM: 'START_STREAM',
};

export class StreamsApi extends ApiClient {
  public static MEDIA_URL = import.meta.env.VITE_MEDIA_URL;

  public static async startStream(cameraId: string, quality: StreamQuality): Promise<boolean> {
    if (!cameraId) {
      return Promise.reject(new Error('[startStream] Missing CameraId parameter'));
    }
    if (!quality) {
      return Promise.reject(new Error('[startStream] Missing Quality parameter'));
    }
    const res = await this.http.post(`/streams/live/${cameraId}/${quality}`, {});
    return res.data;
  }

  public static async getStreamData(cameraId: string, quality: StreamQuality): Promise<StreamData> {
    if (!cameraId) {
      return Promise.reject(new Error('[getStreamData] Missing CameraId parameter'));
    }
    if (!quality) {
      return Promise.reject(new Error('[getStreamData] Missing Quality parameter'));
    }
    const res = await this.http.get(`/streams/live/${cameraId}/${quality}`, {});
    return res.data;
  }

  public static async checkStreamFile(streamId: string, token: string): Promise<boolean> {
    if (!streamId || !token) {
      return Promise.resolve(false);
    }
    const res = await this.http.head(`/streams/live/${streamId}`, {
      params: {
        token: token,
      },
    });
    return res.status === 200;
  }
  public static async updateStreamPreferences(preferences: UpdateStreamPreferences): Promise<StreamPreferences> {
    return this.http.post<StreamPreferences>('/streams/preference', preferences).then((res) => res.data);
  }

  public static async getStreamPreferences(): Promise<StreamPreferences> {
    return this.http.get<StreamPreferences>('/streams/preference').then((res) => res.data);
  }

  public static async updateMediaProvider(patientId: string, mediaProvider: MediaProviderName): Promise<void> {
    await this.http.put(`/patients/${patientId}/media-provider`, { mediaProvider });
  }
}

/**
 * Repoints one patient's cameras at a different streaming backend. Invalidates
 * the cached patient row and list so the form and the list badge re-render with
 * the persisted value.
 */
export function useUpdateMediaProvider(patientId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (mediaProvider: MediaProviderName) => StreamsApi.updateMediaProvider(patientId, mediaProvider),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PatientsApiKeys.Patient, patientId]);
        queryClient.invalidateQueries([PatientsApiKeys.Patients]);
      },
    },
  );
}
export function useStreamData(cameraId: string, quality: StreamQuality, queryOptions?: UseQueryOptions<StreamData>) {
  return useQuery<StreamData>([StreamsApiKeys.GET_STREAM_DATA, cameraId, quality], () => StreamsApi.getStreamData(cameraId, quality), {
    ...queryOptions,
  });
}
export function useStartStream(
  cameraId: string,
  quality: StreamQuality,
  queryOptions?: UseQueryOptions<boolean, boolean>,
) {
  return useQuery<boolean, boolean, boolean>(
    [StreamsApiKeys.START_STREAM, cameraId, quality],
    () => StreamsApi.startStream(cameraId, quality),
    {
      retry: true,
      ...queryOptions,
    },
  );
}

export function useCheckStreamExists(streamData: StreamData, queryOptions?: UseQueryOptions<boolean, boolean>) {
  return useQuery<boolean, boolean, boolean>(
    [StreamsApiKeys.GET_STREAM_STATUS, streamData.streamId],
    () => StreamsApi.checkStreamFile(streamData.streamId, streamData.token),
    {
      retry: true,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
      ...queryOptions,
    },
  );
}

export function useMutateStreamPreferences() {
  return useMutation([StreamsApiKeys.UPDATE_PREFERENCES],(preferences: UpdateStreamPreferences) => StreamsApi.updateStreamPreferences(preferences));
}

export function useStreamPreferences(options?: UseQueryOptions<StreamPreferences>) {
  return useQuery<StreamPreferences>([StreamsApiKeys.GET_PREFERENCES], () => StreamsApi.getStreamPreferences(), options);
}
